import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// Tinder-style card stack for swipe-mode inbox triage. Opened from the inbox
// promo card (or Space on a selected email row); normal row taps navigate to
// the full email thread page instead.
export default class extends Controller {
    STACK_SIZE = 3
    CLAIM_PX = 24
    THRESHOLD_RATIO = 0.3
    FLICK_VELOCITY = 0.5 // px/ms
    ROTATE_DEG_PER_PX = 0.05
    FLY_MS = 300

    initialize() {
        this._onKeydown = this._handleKeydown.bind(this)
        this._onBeforeCache = () => this._teardown()
    }

    connect() {
        this._stack = null
        this._cards = [] // rendered card elements, index 0 = top
        this._queue = [] // descriptors not yet rendered
        this._history = [] // { desc, action, card } for undo, newest last
        this._cache = new Map() // message id -> Promise<html>
        this._swipe = null
        this._acted = false
        document.addEventListener("turbo:before-cache", this._onBeforeCache)
        if (this._consumeSwipeParam()) requestAnimationFrame(() => this.openSwipeMode())
    }

    disconnect() {
        document.removeEventListener("turbo:before-cache", this._onBeforeCache)
        this._teardown()
    }

    _consumeSwipeParam() {
        let params = new URLSearchParams(window.location.search)
        if (params.get("swipe") !== "1") return false
        params.delete("swipe")
        let qs = params.toString()
        history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""))
        return true
    }

    // Swipe-mode entry from the inbox promo card (or keyboard Space in thread-triage).
    openSwipeMode(event) {
        if (event) {
            event.preventDefault()
            event.stopPropagation()
        }
        if (this._stack) return
        let first = this.element.querySelector(".assistant-thread-item[data-message-id]")
        if (first) this.openFromRow(first)
    }

    isOpen() {
        return !!this._stack
    }

    // Public API so thread-triage's keyboard preview (Space/Enter) opens the
    // stack instead of the old modal.
    openFromRow(row) {
        if (this._stack) return
        let rows = Array.from(this.element.querySelectorAll(".assistant-thread-item[data-message-id]")).filter((r) => !r.closest(".is-leaving"))
        let idx = rows.indexOf(row)
        if (idx === -1) return

        this._queue = rows.slice(idx).map((r) => this._descriptorFor(r))
        this._total = this._queue.length
        this._history = []
        this._acted = false
        this._buildStack()
        this._fillStack()
        this._updateChrome()
        document.addEventListener("keydown", this._onKeydown)
    }

    close() {
        if (!this._stack) return
        let acted = this._acted
        this._teardown()
        // The hidden list is stale after any action; a reload is the simplest
        // way to reflect archived/trashed rows (and any undone ones).
        if (acted) window.location.reload()
    }

    // ----- Setup / teardown -----

    _descriptorFor(row) {
        let d = row.dataset
        return {
            id: d.messageId,
            previewUrl: d.emailPreviewUrl,
            archiveUrl: d.rowSwipeUrl,
            draftUrl: d.draftUrl,
            starUrl: d.starUrl,
            unstarUrl: d.unstarUrl,
            trashUrl: d.trashUrl,
            untrashUrl: d.untrashUrl,
            unarchiveUrl: d.unarchiveUrl,
            starred: false
        }
    }

    _phone() {
        return this.element.closest(".assistant-phone")
    }

    _buildStack() {
        let stack = document.createElement("div")
        stack.className = "card-stack"
        stack.innerHTML = `
            <div class="card-stack-top">
                <button type="button" class="card-stack-close" aria-label="Back to Messages"><i class="bi bi-x-lg"></i></button>
                <span class="card-stack-count"></span>
            </div>
            <div class="card-stack-viewport"></div>
            <div class="card-stack-bar">
                <button type="button" class="stack-btn stack-btn--small stack-btn--undo" aria-label="Undo" title="Undo"><i class="bi bi-arrow-counterclockwise"></i></button>
                <button type="button" class="stack-btn stack-btn--draft" aria-label="Draft reply" title="Draft reply"><i class="bi bi-pencil-fill"></i></button>
                <button type="button" class="stack-btn stack-btn--small stack-btn--star" aria-label="Star" title="Star"><i class="bi bi-star-fill"></i></button>
                <button type="button" class="stack-btn stack-btn--archive" aria-label="Archive" title="Archive"><i class="bi bi-check-lg"></i></button>
                <button type="button" class="stack-btn stack-btn--small stack-btn--skip" aria-label="Skip" title="Skip to next"><i class="bi bi-chevron-right"></i></button>
            </div>`

        stack.querySelector(".card-stack-close").addEventListener("click", () => this.close())
        stack.querySelector(".stack-btn--undo").addEventListener("click", (e) => { e.stopPropagation(); this._undo() })
        stack.querySelector(".stack-btn--draft").addEventListener("click", (e) => { e.stopPropagation(); this._act("draft") })
        stack.querySelector(".stack-btn--star").addEventListener("click", (e) => { e.stopPropagation(); this._toggleStar() })
        stack.querySelector(".stack-btn--archive").addEventListener("click", (e) => { e.stopPropagation(); this._act("archive") })
        stack.querySelector(".stack-btn--skip").addEventListener("click", (e) => { e.stopPropagation(); this._skip() })

        let viewport = stack.querySelector(".card-stack-viewport")
        viewport.addEventListener("touchstart", (e) => this._touchStart(e), { passive: true })
        viewport.addEventListener("touchmove", (e) => this._touchMove(e), { passive: false })
        viewport.addEventListener("touchend", () => this._touchEnd())
        viewport.addEventListener("touchcancel", () => this._touchEnd({ cancel: true }))
        viewport.addEventListener("pointerdown", (e) => this._pointerDown(e))
        viewport.addEventListener("pointermove", (e) => this._pointerMove(e))
        viewport.addEventListener("pointerup", (e) => this._pointerUp(e))
        viewport.addEventListener("pointercancel", (e) => this._pointerUp(e, { cancel: true }))

        let phone = this._phone()
        let nav = phone ? phone.querySelector(".assistant-bottom-nav") : null
        if (phone && nav) {
            phone.insertBefore(stack, nav)
            phone.classList.add("is-stack-open")
        } else {
            // Fallback (shouldn't happen): mount over everything.
            document.body.appendChild(stack)
        }
        this._stack = stack
        this._viewport = viewport
    }

    _teardown() {
        if (!this._stack) return
        document.removeEventListener("keydown", this._onKeydown)
        let phone = this._phone()
        if (phone) phone.classList.remove("is-stack-open")
        this._stack.remove()
        this._stack = null
        this._viewport = null
        this._cards = []
        this._queue = []
        this._history = []
        this._swipe = null
    }

    // ----- Stack management -----

    _fillStack() {
        while (this._cards.length < this.STACK_SIZE && this._queue.length) {
            let desc = this._queue.shift()
            let card = this._createCard(desc)
            this._viewport.appendChild(card)
            this._cards.push(card)
        }
        this._layout()
        // Warm the cache one email beyond the rendered stack so the card
        // created on the next promote already has its body.
        if (this._queue.length) this._fetchBody(this._queue[0])
        if (!this._cards.length) this._showEmpty()
    }

    _createCard(desc) {
        let card = document.createElement("div")
        card.className = "stack-card"
        card.dataset.messageId = desc.id
        card.innerHTML = `
            <div class="stack-card-content"><p class="email-modal-loading">Loading email…</p></div>
            <span class="stack-badge stack-badge--archive"><i class="bi bi-archive"></i> Archive</span>
            <span class="stack-badge stack-badge--draft"><i class="bi bi-pencil"></i> Draft</span>
            <span class="stack-card-star"><i class="bi bi-star-fill"></i></span>`
        card._desc = desc
        this._fetchBody(desc).then((html) => {
            if (html === null) return
            let content = card.querySelector(".stack-card-content")
            content.innerHTML = html
            this._autosizeFrame(content.querySelector("iframe[data-email-frame]"))
        })
        return card
    }

    _fetchBody(desc) {
        if (!this._cache.has(desc.id)) {
            let promise = fetch(desc.previewUrl, { headers: { Accept: "text/html" }, credentials: "same-origin" })
                .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
                .catch(() => null)
            this._cache.set(desc.id, promise)
        }
        return this._cache.get(desc.id).then((html) => {
            if (html === null) return '<p class="email-modal-loading">Could not load this email.</p>'
            return html
        })
    }

    _layout() {
        this._cards.forEach((card, i) => {
            card.style.setProperty("--stack-depth", i)
            card.style.zIndex = 30 - i
            card.classList.toggle("is-top", i === 0)
        })
    }

    _topCard() {
        return this._cards[0] || null
    }

    _updateChrome() {
        if (!this._stack) return
        let remaining = this._cards.length + this._queue.length
        let counter = this._stack.querySelector(".card-stack-count")
        if (remaining) {
            counter.textContent = `${this._total - remaining + 1} of ${this._total}`
        } else {
            counter.textContent = ""
        }
        let undoBtn = this._stack.querySelector(".stack-btn--undo")
        undoBtn.disabled = !this._history.length
        let hasTop = !!this._topCard()
        ;["draft", "star", "archive", "skip"].forEach((name) => {
            this._stack.querySelector(`.stack-btn--${name}`).disabled = !hasTop
        })
        let star = this._stack.querySelector(".stack-btn--star")
        let top = this._topCard()
        star.classList.toggle("is-starred", !!(top && top._desc.starred))
    }

    _showEmpty() {
        if (!this._viewport || this._viewport.querySelector(".card-stack-empty")) return
        let empty = document.createElement("div")
        empty.className = "card-stack-empty"
        empty.innerHTML = `
            <div class="card-stack-empty-icon"><i class="bi bi-check2-circle"></i></div>
            <h3>All caught up</h3>
            <p>You've been through every email in this view.</p>
            <button type="button" class="card-stack-empty-btn">Back to Messages</button>`
        empty.querySelector(".card-stack-empty-btn").addEventListener("click", () => this.close())
        this._viewport.appendChild(empty)
    }

    _hideEmpty() {
        let empty = this._viewport && this._viewport.querySelector(".card-stack-empty")
        if (empty) empty.remove()
    }

    // ----- Actions -----

    // action: "archive" | "draft" (star is separate — no fly-off).
    _act(action, opts = {}) {
        let card = this._topCard()
        if (!card || card.dataset.stackFlying) return
        let desc = card._desc

        let url = { archive: desc.archiveUrl, draft: desc.draftUrl }[action]
        this._post(url)
        this._acted = true
        this._history.push({ desc: desc, action: action, card: card })

        if (!opts.fromSwipe) this._flashBadge(card, action)
        this._flyOff(card, action)
    }

    _post(url) {
        if (!url) return
        fetch(url, {
            method: "POST",
            headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        }).catch(() => {})
    }

    _flashBadge(card, action) {
        let badge = card.querySelector(`.stack-badge--${action}`)
        if (!badge) return
        badge.style.transition = "opacity 0.15s ease"
        badge.style.opacity = "1"
    }

    _flyOff(card, action) {
        card.dataset.stackFlying = "1"
        card.classList.remove("is-dragging")
        let w = card.offsetWidth
        let transform
        if (action === "archive") {
            transform = `translateX(${window.innerWidth + w}px) rotate(24deg)`
        } else if (action === "draft") {
            transform = `translateX(${-(window.innerWidth + w)}px) rotate(-24deg)`
        } else if (action === "skip") {
            transform = `translateY(${-(window.innerHeight * 0.6)}px) rotate(6deg)`
        }
        card.style.transition = `transform ${this.FLY_MS / 1000}s cubic-bezier(0.22, 0.61, 0.36, 1), opacity ${this.FLY_MS / 1000}s ease-out`
        card.style.transform = transform
        card.style.opacity = "0"
        this._cards.shift()
        this._layout()
        this._updateChrome()
        setTimeout(() => {
            // An undo inside the animation window clears the flying flag and
            // re-adopts the card — leave it alone in that case.
            if (card.dataset.stackFlying) card.remove() // kept alive via the history entry for undo
            this._fillStack()
            this._updateChrome()
        }, this.FLY_MS)
    }

    // Advance without archive/draft — no server call, not undoable via API.
    _skip(opts = {}) {
        let card = this._topCard()
        if (!card || card.dataset.stackFlying) return
        let desc = card._desc
        this._history.push({ desc: desc, action: "skip", card: card })
        this._flyOff(card, "skip", opts)
    }

    _toggleStar() {
        let card = this._topCard()
        if (!card || card.dataset.stackFlying) return
        let desc = card._desc
        let starring = !desc.starred
        desc.starred = starring
        this._post(starring ? desc.starUrl : desc.unstarUrl)
        this._acted = true
        this._history.push({ desc: desc, action: starring ? "star" : "unstar", card: card })

        card.classList.toggle("is-starred", starring)
        if (starring) {
            card.classList.remove("is-star-pulse")
            void card.offsetWidth // restart the pulse animation
            card.classList.add("is-star-pulse")
        }
        this._updateChrome()
    }

    _undo() {
        let entry = this._history.pop()
        if (!entry) return
        if (entry.action !== "skip") {
            let inverse = { archive: entry.desc.unarchiveUrl, star: entry.desc.unstarUrl, unstar: entry.desc.starUrl }[entry.action]
            this._post(inverse) // draft has no inverse: the generated draft is kept
        }

        if (entry.action === "star" || entry.action === "unstar") {
            // The card never left the stack; just flip the state back.
            entry.desc.starred = entry.action === "unstar"
            entry.card.classList.toggle("is-starred", entry.desc.starred)
            this._updateChrome()
            return
        }

        this._hideEmpty()

        // Keep the stack at STACK_SIZE: push the deepest card back to the queue.
        if (this._cards.length >= this.STACK_SIZE) {
            let deepest = this._cards.pop()
            this._queue.unshift(deepest._desc)
            deepest.remove()
        }

        let card = entry.card
        delete card.dataset.stackFlying
        card.querySelectorAll(".stack-badge").forEach((b) => {
            b.style.transition = ""
            b.style.opacity = ""
        })
        // Re-enter from the fly-off position: the element still carries its
        // exit transform inline, so mount it, force a layout, then release
        // the inline styles and let the depth-0 CSS transform take over.
        card.style.transition = "none"
        this._viewport.appendChild(card)
        this._cards.unshift(card)
        this._layout()
        void card.offsetWidth
        card.style.transition = `transform ${this.FLY_MS / 1000}s cubic-bezier(0.22, 0.61, 0.36, 1), opacity ${this.FLY_MS / 1000}s ease-out`
        card.style.transform = ""
        card.style.opacity = ""
        setTimeout(() => {
            card.style.transition = ""
        }, this.FLY_MS)
        this._updateChrome()
    }

    // ----- Touch swipe on the top card (ported from the modal swipe in
    // thread-triage: follow-the-finger translate + rotation, badges fade in
    // with progress, past-threshold or flick flies off, otherwise springs
    // back; clearly vertical drags stay with the scrollable email body) -----

    _touchStart(e) {
        if (e.touches.length !== 1) return
        this._beginSwipe(e.touches[0].clientX, e.touches[0].clientY, e.timeStamp)
    }

    _pointerDown(e) {
        if (e.pointerType === "touch" || !e.isPrimary || e.button !== 0) return
        if (e.target.closest(".stack-btn, .card-stack-close")) return
        this._beginSwipe(e.clientX, e.clientY, e.timeStamp, { pointerId: e.pointerId, captureTarget: e.currentTarget })
    }

    _beginSwipe(clientX, clientY, timeStamp, opts = {}) {
        let card = this._topCard()
        if (!card || card.dataset.stackFlying) return
        this._swipe = {
            card: card,
            startX: clientX,
            startY: clientY,
            width: card.offsetWidth,
            claimed: false,
            dx: 0,
            lastX: clientX,
            lastT: timeStamp,
            velocity: 0,
            raf: null,
            pointerId: opts.pointerId || null,
            captureTarget: opts.captureTarget || null
        }
        if (this._swipe.captureTarget && this._swipe.pointerId != null) {
            try { this._swipe.captureTarget.setPointerCapture(this._swipe.pointerId) } catch { /* detached */ }
        }
    }

    _touchMove(e) {
        this._moveSwipe(e.touches[0].clientX, e.touches[0].clientY, e.timeStamp, e)
    }

    _pointerMove(e) {
        if (!this._swipe || e.pointerType === "touch" || e.pointerId !== this._swipe.pointerId) return
        this._moveSwipe(e.clientX, e.clientY, e.timeStamp, e)
    }

    _moveSwipe(x, y, timeStamp, e) {
        let s = this._swipe
        if (!s) return
        let dx = x - s.startX
        let dy = y - s.startY

        if (!s.claimed) {
            if (Math.abs(dy) > 14 && Math.abs(dy) > Math.abs(dx)) {
                this._swipe = null
                return
            }
            if (Math.abs(dx) < this.CLAIM_PX || Math.abs(dx) < Math.abs(dy) * 1.2) return
            s.claimed = true
            s.card.classList.add("is-dragging")
        }

        e.preventDefault()
        if (timeStamp > s.lastT) s.velocity = (x - s.lastX) / (timeStamp - s.lastT)
        s.lastX = x
        s.lastT = timeStamp
        s.dx = dx
        if (!s.raf) {
            s.raf = requestAnimationFrame(() => {
                s.raf = null
                this._renderDrag(s)
            })
        }
    }

    _renderDrag(s) {
        let dx = s.dx
        let progress = Math.min(Math.abs(dx) / (s.width * this.THRESHOLD_RATIO), 1)
        s.card.style.transform = `translateX(${dx}px) rotate(${(dx * this.ROTATE_DEG_PER_PX).toFixed(2)}deg)`
        s.card.style.opacity = `${1 - 0.25 * progress}`
        let archive = s.card.querySelector(".stack-badge--archive")
        let draft = s.card.querySelector(".stack-badge--draft")
        if (archive) archive.style.opacity = dx > 0 ? progress : 0
        if (draft) draft.style.opacity = dx < 0 ? progress : 0
    }

    _touchEnd(opts = {}) {
        this._endSwipe(opts)
    }

    _pointerUp(e, opts = {}) {
        if (!this._swipe || e.pointerType === "touch" || e.pointerId !== this._swipe.pointerId) return
        this._endSwipe(opts)
    }

    _endSwipe(opts = {}) {
        let s = this._swipe
        this._swipe = null
        if (!s || !s.claimed) return
        if (s.raf) cancelAnimationFrame(s.raf)
        this._suppressNextClick()

        let pastThreshold = Math.abs(s.dx) >= s.width * this.THRESHOLD_RATIO
        let flick = Math.abs(s.velocity) >= this.FLICK_VELOCITY && (s.velocity > 0) === (s.dx > 0)
        if (!opts.cancel && (pastThreshold || flick)) {
            this._act(s.dx > 0 ? "archive" : "draft", { fromSwipe: true })
        } else {
            this._springBack(s)
        }
    }

    _springBack(s) {
        s.card.classList.remove("is-dragging")
        s.card.style.transform = ""
        s.card.style.opacity = ""
        s.card.querySelectorAll(".stack-badge").forEach((b) => {
            b.style.transition = "opacity 0.2s ease"
            b.style.opacity = "0"
        })
        setTimeout(() => {
            s.card.querySelectorAll(".stack-badge").forEach((b) => (b.style.transition = ""))
        }, 220)
    }

    // A completed drag must not double as a tap on the card or the bar.
    _suppressNextClick() {
        let swallow = (e) => {
            e.stopPropagation()
            e.preventDefault()
        }
        document.addEventListener("click", swallow, { capture: true, once: true })
        setTimeout(() => document.removeEventListener("click", swallow, { capture: true }), 400)
    }

    // ----- Keyboard (ArrowRight archive, ArrowLeft draft, Escape close; s star,
    // n / ArrowDown skip, z / u undo) -----

    _handleKeydown(e) {
        if (!this._stack) return
        if (e.metaKey || e.ctrlKey || e.altKey) return
        let t = e.target
        if (t && t.matches && (t.matches("input, textarea, select") || t.isContentEditable)) return

        let key = e.key
        if (key === "Escape") {
            e.preventDefault()
            this.close()
        } else if (key === "ArrowRight" || key === "e") {
            e.preventDefault()
            this._act("archive")
        } else if (key === "ArrowLeft") {
            e.preventDefault()
            this._act("draft")
        } else if (key === "s") {
            e.preventDefault()
            this._toggleStar()
        } else if (key === "n" || key === "ArrowDown") {
            e.preventDefault()
            this._skip()
        } else if (key === "z" || key === "u") {
            e.preventDefault()
            this._undo()
        }
    }

    // ----- Iframe autosize (email-body controller handles this on static pages;
    // card stack injects HTML dynamically and calls this directly) -----

    _autosizeFrame(frame) {
        if (!frame) return
        let resize = () => {
            try {
                let doc = frame.contentDocument
                if (!doc || !doc.documentElement) return
                let height = Math.max(doc.documentElement.scrollHeight, doc.body ? doc.body.scrollHeight : 0)
                if (height > 0) frame.style.height = `${height + 24}px`
            } catch {
                // Cross-origin access denied — keep the CSS fallback height.
            }
        }
        frame.addEventListener("load", () => {
            resize()
            setTimeout(resize, 300)
            setTimeout(resize, 1200)
        })
        resize()
    }

}
